/**
 * Cookie Utility Functions
 * Helper functions for reading and writing browser cookies
 */

/**
 * Extract root domain from hostname to allow cookies on all subdomains
 * Examples:
 * - sit.scenepro.ca -> .scenepro.ca
 * - sit-app.scenepro.ca -> .scenepro.ca
 * - localhost -> localhost
 */
const getRootDomain = (): string => {
  const hostname = window.location.hostname;
  const domainParts = hostname.split('.');

  // If hostname has more than 2 parts (e.g., sit.scenepro.ca), extract root domain
  // Otherwise, use hostname as-is (e.g., localhost)
  return domainParts.length > 2
    ? `.${domainParts.slice(-2).join('.')}`
    : hostname;
};

/**
 * Get cookie value by name
 */
export const getCookieValue = (name: string): string | null => {
  const matches = document.cookie.match(new RegExp(
    '(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'
  ));
  return matches ? decodeURIComponent(matches[1]) : null;
};

/**
 * Set a cookie value for 6 months (182 days) by default
 * The cookie is set for all subdomains
 */
export const setCookie = (name: string, value: string, days: number = 182): void => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  const rootDomain = getRootDomain();

  document.cookie = `${name}=${value};${expires};path=/;domain=${rootDomain};SameSite=Lax`;
};

/**
 * Delete a cookie by name
 * Deletes the cookie for all subdomains
 */
export const deleteCookie = (name: string): void => {
  const rootDomain = getRootDomain();
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${rootDomain};`;
};

